"use client";
import { useState } from "react";
import * as React from "react";



export default function InteriorBanner() {
  // Navigation links data
  const breadcrumbs = [
    { label: "AetherSIM", href: "/", active: false },
    { label: "Country Directory", href: "/country-directory", active: true },
  ];

  // Example country data with flags and descriptions
  const countries = [
    {
      name: "United States",
      href: "/usa-1",
      flag: "https://img.b2bpic.net/premium-photo/american-flag-with-cityscape-background-with-futuristic-digital-style_1234489-3227.jpg?w=647",
      description: "Fast 5G, best coverage, unlimited data options.",
      featured: true,
    },
    {
      name: "Canada",
      href: "/canada",
      flag: "https://img.b2bpic.net/premium-photo/red-maple-leaf-is-blue-blanket-with-dark-background_7023-22904.jpg?w=740",
      description: "Nationwide LTE, flexible plans, traveler-friendly.",
      featured: false,
    },
    {
      name: "Mexico",
      href: "/mex",
      flag: "https://img.b2bpic.net/premium-photo/mexico-independent-day-poster-template-with-flag-background_1252117-2846.jpg?w=740",
      description: "Affordable rates, great for vacationers.",
      featured: false,
    },
    {
      name: "Ukraine",
      href: "/ukraine",
      flag: "https://img.b2bpic.net/free-vector/ukraine-colorful-flag-heart-shape-with-brush-stroke-style-isolated-white-background_90220-732.jpg?w=740",
      description: "Reliable 4G, instant activation.",
      featured: false,
    },
    {
      name: "United Kingdom",
      href: "/united-kingdom",
      flag: "https://img.b2bpic.net/free-vector/united-kingdom-flag-design_1034-194.jpg?w=740",
      description: "Superfast 5G, roaming included.",
      featured: true,
    },
    {
      name: "India",
      href: "/india",
      flag: "https://img.b2bpic.net/premium-photo/symbol-national-flag-with-word-indian-it_847859-4597.jpg?w=740",
      description: "Best value, huge data packs.",
      featured: false,
    },
    {
      name: "UAE",
      href: "/uae",
      flag: "https://img.b2bpic.net/premium-vector/kuwait-flag-official-colors-vector-illustration_601298-4570.jpg?w=740",
      description: "Premium networks, instant eSIM.",
      featured: false,
    },
  ];

  // Search/filter state
  const [search, setSearch] = React.useState("");
  const filteredCountries = countries.filter((c) =>
    c.name.toLowerCase().includes(search.trim().toLowerCase()),
  );

  // Sorting by featured then name
  const sortedCountries = [...filteredCountries].sort((a, b) => {
    if (a.featured && !b.featured) return -1;
    if (!a.featured && b.featured) return 1;
    return a.name.localeCompare(b.name);
  });

  return (
    <section className="pt-20 pb-12 w-full border-b bg-card relative overflow-hidden shadow-lg">
      {/* Enhanced background image only, no green lines or sun graphics */}
      <div className="pointer-events-none absolute inset-0 z-0 flex items-center justify-center">
        <img
          src="https://images.unsplash.com/photo-1506744038136-46273834b3fb?auto=format&fit=crop&w=1600&q=80"
          alt=""
          className="absolute inset-0 w-full h-full object-cover select-none brightness-110 contrast-110 saturate-150"
          draggable="false"
          aria-hidden="true"
          style={{ imageRendering: "auto" }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-transparent opacity-70" />
      </div>
      {/* Shop for ESIMs Heading */}
      <div className="w-full max-w-7xl mx-auto px-4 lg:px-0 flex flex-col items-center gap-2 relative z-10">
        <h2 className="text-lg md:text-2xl font-bold text-card-foreground mb-2">
          <span className="lg:text-[1.65rem] dark:text-[#3b82f6]">
            Shop for ESIMs now
          </span>
        </h2>
      </div>
      <div className="w-full max-w-7xl mx-auto px-4 lg:px-0 flex flex-col items-center gap-6 relative z-10">
        {/* Main title */}
        <h1 className="text-2xl md:text-4xl lg:text-5xl font-black text-card-foreground leading-tight text-center mb-2 drop-shadow-sm">
          <span className="dark:text-[#000000] font-black">
            Select Your Country
          </span>
        </h1>

        {/* Breadcrumbs */}
        <nav
          aria-label="Breadcrumb"
          className="flex items-center justify-center mb-2"
        >
          {breadcrumbs.map((crumb, index) => (
            <div
              key={index}
              className="flex items-center text-muted-foreground"
            >
              <a
                href={crumb.href}
                className={`text-sm lg:text-base font-medium transition-colors ${
                  crumb.active ? "text-primary font-bold" : "hover:text-primary"
                }`}
              >
                <span className="lg:text-[1.15rem] dark:text-[#000000]">
                  {crumb.label}
                </span>
              </a>
              {index < breadcrumbs.length - 1 && (
                <div className="w-4 h-4 flex items-center justify-center mx-1 lg:mx-2">
                  <svg
                    width="14"
                    height="14"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    className="text-muted-foreground"
                    viewBox="0 0 24 24"
                  >
                    <path
                      d="M9 18l6-6-6-6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              )}
            </div>
          ))}
        </nav>

        {/* Advanced features: search/filter, sort, grid */}
        <div className="flex flex-col md:flex-row gap-3 w-full justify-between items-center mb-3">
          {/* Search input */}
          <div className="w-full max-w-xl">
            <span htmlFor="country-search" className="sr-only">
              Search countries
            </span>
            <div className="relative">
              <input
                id="country-search"
                type="text"
                placeholder="Search countries..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full rounded-lg border border-muted bg-background py-3 pl-12 pr-4 text-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 shadow-md"
                autoComplete="off"
              />
              <svg
                className="absolute left-4 top-3.5 text-muted-foreground"
                width="20"
                height="20"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                viewBox="0 0 24 24"
              >
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
            </div>
          </div>
          {/* Sort info */}
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <svg
              width="15"
              height="15"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
              className="mr-1"
            >
              <path d="M3 6h18M3 12h12M3 18h6" />
            </svg>
            <span>
              <span>{sortedCountries.length}</span>countries{" "}
              <span className="hidden md:inline">displayed</span>
            </span>
          </div>
        </div>

        {/* Responsive country grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5 w-full">
          {sortedCountries.map((country) => (
            <a
              href={country.href}
              key={country.href}
              className={`group flex flex-col items-center justify-center gap-2 px-4 py-4 rounded-xl bg-card border shadow-md transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-primary/40 relative min-h-[6rem] ring-1 ring-inset ring-muted/30 hover:bg-primary/10 hover:border-primary/40 hover:shadow-xl active:scale-[0.98] ${
                country.featured ? "border-primary shadow-2xl" : ""
              }`}
              style={{ WebkitTapHighlightColor: "transparent" }}
            >
              {/* Flag */}
              <img
                src={country.flag}
                alt={country.name + " flag"}
                className="w-10 h-10 rounded-full border border-muted object-cover shadow-md mb-1"
                style={{ imageRendering: "auto" }}
              />
              {/* Country name */}
              <span className="font-semibold text-lg text-foreground group-hover:text-primary transition-colors text-center">
                <span>{country.name}</span>
              </span>
              {/* Description */}
              <span className="text-xs text-muted-foreground text-center">
                <span>{country.description}</span>
              </span>
              {/* Featured badge, only shows visual empty badge for spacing */}
              {country.featured && (
                <span className="absolute top-2.5 right-2.5 px-2 py-0.5 rounded-full bg-transparent"></span>
              )}
            </a>
          ))}
          {sortedCountries.length === 0 && (
            <div className="col-span-full flex flex-col items-center justify-center py-8">
              <svg
                width="30"
                height="30"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                viewBox="0 0 24 24"
                className="mb-3 text-muted-foreground"
              >
                <circle cx="12" cy="12" r="10" />
                <path d="M8 15s1.5 2 4 2 4-2 4-2" />
                <line x1="9" y1="9" x2="9.01" y2="9" />
                <line x1="15" y1="9" x2="15.01" y2="9" />
              </svg>
              <span className="text-muted-foreground text-base font-medium">
                No countries found.
              </span>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
